import React, { useEffect, useRef, useState } from "react";
import { Car, Plane, Home, Heart, Building, FileText, Building2, Activity, MessageSquareMore } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from "./ui/card";

interface InsuranceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  className?: string;
  style?: React.CSSProperties;
}

const InsuranceCard: React.FC<InsuranceCardProps> = ({ title, description, icon, path, className, style }) => {
  return (
    <Link to={path} className="block h-full">
      <Card
        className={`h-full relative overflow-hidden rounded-xl group transform-gpu bg-white [box-shadow:0_0_0_1px_rgba(0,0,0,.03),0_2px_4px_rgba(0,0,0,.05),0_12px_24px_rgba(0,0,0,.05)] transition-all duration-300 dark:bg-black dark:[border:1px_solid_rgba(255,255,255,.1)] dark:[box-shadow:0_-20px_80px_-20px_#ffffff1f_inset] ${className || ''}`}
        style={style}
      >
        <CardContent className="p-6 flex flex-col items-center h-full z-10 pointer-events-none">
          <div className="text-[#cc2c32] mb-4">
            {icon}
          </div>

          <div className="flex-grow flex flex-col justify-center">
            <h3 className="text-lg font-light mb-2 text-center text-[#21282d] transition-colors duration-300 group-hover:text-feijo-red">
              {title}
            </h3>
            <p className="text-center text-sm font-light text-[#21282d] transition-colors duration-300 group-hover:text-feijo-red line-clamp-3">
              {description}
            </p>
          </div>
        </CardContent>
        {/* Hover overlay effect to match BentoCard style */}
        <div className="pointer-events-none absolute inset-0 transform-gpu transition-all duration-300 group-hover:bg-black/[.03] group-hover:dark:bg-neutral-800/10" />
      </Card>
    </Link>
  );
};

const consultants = [
  { name: "Carlos Henrique", phone: "5522988156269" },
  { name: "Felipe", phone: "5521972110705" },
  { name: "Gabriel", phone: "5522997501988" },
  { name: "Renan", phone: "5522988521503" },
  { name: "Renata", phone: "5511994150565" },
];

const InsuranceSection = () => {
  const [showModal, setShowModal] = useState(false);
  const [startFade, setStartFade] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    let fallbackTimer: number | undefined;
    const onScroll = () => {
      if (!hasScrolled && window.scrollY > 8) {
        setHasScrolled(true);
        if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
        window.removeEventListener("scroll", onScroll);
      }
    };
    if (!hasScrolled) {
      window.addEventListener("scroll", onScroll, { passive: true });
      fallbackTimer = window.setTimeout(() => {
        setStartFade(true);
        if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
        window.removeEventListener("scroll", onScroll);
      }, 3000);
    }
    const node = sectionRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e.isIntersecting && (hasScrolled || e.intersectionRatio >= 0.7)) {
          setStartFade(true);
          if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
          obs.disconnect();
        }
      },
      { threshold: [0.25, 0.5, 0.7, 0.9] }
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
      if (!hasScrolled) window.removeEventListener("scroll", onScroll);
      if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
    };
  }, [hasScrolled]);
  const insuranceTypes = [
    {
      title: "Seguro de Automóveis",
      description: "Proteção completa para seu veículo contra danos, roubo e acidentes.",
      icon: <Car size={40} strokeWidth={1.5} color="#cc2c32" />,
      path: "/seguros/auto"
    },
    {
      title: "Seguro Viagem",
      description: "Viaje com tranquilidade, com cobertura médica e assistência 24h.",
      icon: <Plane size={40} strokeWidth={1.5} color="#cc2c32" />,
      path: "/seguros/viagem"
    },
    {
      title: "Seguro Residencial",
      description: "Proteja sua casa contra incêndio, roubo e danos estruturais.",
      icon: <Home size={40} strokeWidth={1.5} color="#cc2c32" />,
      path: "/seguros/residencial"
    },
    {
      title: "Seguro de Vida",
      description: "Garanta o futuro financeiro de quem você ama em qualquer situação.",
      icon: <Heart size={40} strokeWidth={1.5} color="#cc2c32" />,
      path: "/seguros/vida"
    },
    {
      title: "RC Obras Civis",
      description: "Proteção contra danos causados a terceiros durante obras civis.",
      icon: <Building size={40} strokeWidth={1.5} color="#cc2c32" />,
      path: "/seguros/obras-civis"
    },
    {
      title: "Seguro Garantia",
      description: "Seguro garantia para processos de licitação e contratos públicos.",
      icon: <FileText size={40} strokeWidth={1.5} color="#cc2c32" />,
      path: "/seguros/garantia"
    },
    {
      title: "Seguro Empresarial",
      description: "Proteção completa para o seu negócio, equipamentos e instalações.",
      icon: <Building2 size={40} strokeWidth={1.5} color="#cc2c32" />,
      path: "/seguros/empresarial"
    },
    {
      title: "Plano de Saúde",
      description: "Planos de saúde completos para você e sua família.",
      icon: <Activity size={40} strokeWidth={1.5} color="#cc2c32" />,
      path: "/seguros/saude"
    }
  ];

  return (
    <section ref={sectionRef} id="services" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2 className={`text-2xl md:text-3xl font-light mb-12 text-center text-[#21282d] ${startFade ? "fade-up" : "pre-fade"}`} style={{ ["--fade-delay" as any]: "0ms" }}>Faça sua cotação aqui!</h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {insuranceTypes.map((insurance, index) => (
            <InsuranceCard
              key={index}
              title={insurance.title}
              description={insurance.description}
              icon={insurance.icon}
              path={insurance.path}
              {...{ style: { ["--fade-delay" as any]: `${100 + index * 120}ms` } }}
              {...{ className: `${startFade ? "fade-up" : "pre-fade"}` }}
            />
          ))}
        </div>
        <div className="w-full flex justify-center mt-8">
          <Button
            className={`${startFade ? "fade-up" : "pre-fade"} w-full sm:w-auto px-6 py-4 bg-[#cc2c32] text-white gap-2 transition duration-300 ease-in-out hover:bg-[#b02429]`}
            style={{ ["--fade-delay" as any]: "500ms" }}
            onClick={() => setShowModal(true)}
          >
            <img
              src="/whatsapp-white.svg"
              width="20"
              height="20"
              className="mr-2"
              alt="WhatsApp"
            />
            Para outros seguros, entre em contato.
          </Button>
        </div>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-xs">
              <h3 className="text-lg font-semibold mb-4 text-center text-feijo-darkgray">Selecione o seu Consultor / Corretor:</h3>
              <div className="flex flex-col gap-2">
                {consultants.map((c) => (
                  <a
                    key={c.phone}
                    href={`https://wa.me/${c.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-2 bg-[#dadadb] text-feijo-red rounded hover:bg-gray-400 text-center transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    <MessageSquareMore size={16} />
                    {c.name}
                  </a>
                ))}
              </div>
              <button
                className="mt-4 w-full px-4 py-2 bg-feijo-red text-white rounded hover:bg-gray-400 transition-colors duration-200"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default InsuranceSection;
